import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    Box,
    CardActions,
    CircularProgress,
    Divider,
    FormControlLabel,
    Switch,
    Typography,
    styled
} from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

import DialogWrapper from '@libComponents/Dialog';
import { Button } from '@libComponents';
import { usePermission } from 'permissions';
import { setSnack } from 'utilities/redux.actions';
import { exportEmployeesXlsx } from 'employee/utils/employeeExcel';
import ExportOptionsFields, {
    GroupLabel,
    useExportOptions
} from './exportOptions';

const EXPORT_ALL_PERMISSION = 'employee.export all employees';

const StyledCardAction = styled(CardActions)(({ theme: { spacing } }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    paddingTop: spacing(3),
    paddingBottom: 0
}));

const FILTER_LABELS = {
    branch: 'Branch',
    department: 'Department',
    designation: 'Designation',
    employeeCategory: 'Category',
    gender: 'Gender',
    shift: 'Shift'
};

/**
 * "Export Excel" for the whole employee directory. Lives in the list-page
 * header; applies the page's current filters + search. The base selection is
 * active employees — the two toggles add resigned and account-disabled ones.
 * Only rendered for users holding `employee.export all employees`.
 */
export default function ExportAllExcel({
    filters = {},
    searchTerm = '',
    totalRows = 0
}) {
    const canExportAll = usePermission(EXPORT_ALL_PERMISSION);

    const [open, setOpen] = useState(false);
    const [includeResigned, setIncludeResigned] = useState(false);
    const [includeInactive, setIncludeInactive] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [progress, setProgress] = useState('');
    const optionsState = useExportOptions();

    const dispatch = useDispatch();

    if (!canExportAll) return null;

    const appliedFilterNames = Object.entries(FILTER_LABELS)
        .filter(([key]) => {
            const v = filters[key];
            return v !== undefined && v !== null && v !== '';
        })
        .map(([, label]) => label);
    if (searchTerm) appliedFilterNames.push('Search');

    const handleClose = () => {
        if (!exporting) {
            setOpen(false);
            setProgress('');
        }
    };

    const handleExport = () => {
        if (exporting || !optionsState.selectedCount) return;
        setExporting(true);
        setProgress('Preparing export…');
        exportEmployeesXlsx({
            filters,
            searchTerm,
            includeResigned,
            includeInactive,
            options: optionsState.options,
            onProgress: setProgress,
            onError: (message) => {
                dispatch(setSnack({ message, type: 'error' }));
                setProgress('');
            },
            onSuccess: () => {
                setOpen(false);
                setProgress('');
                dispatch(
                    setSnack({
                        message: 'Employee directory Excel downloaded.',
                        type: 'success'
                    })
                );
            }
        }).finally(() => setExporting(false));
    };

    return (
        <>
            <Button
                variant='outlined'
                size='small'
                color='primary'
                type='button'
                onClick={() => setOpen(true)}
                startIcon={<FileDownloadOutlinedIcon color='primary' />}
                sx={{
                    height: '34px',
                    minHeight: '34px',
                    width: 'auto',
                    px: 2,
                    whiteSpace: 'nowrap'
                }}
            >
                Export Excel
            </Button>

            <DialogWrapper
                open={open}
                onClose={handleClose}
                sx={{ textAlign: 'center' }}
            >
                <Typography variant='h6' sx={{ mb: 1 }}>
                    Export Employee Directory
                </Typography>
                <Divider sx={{ mb: 1 }} />

                <Box sx={{ textAlign: 'left', px: 2 }}>
                    <Typography variant='body2' color='text.secondary'>
                        {appliedFilterNames.length
                            ? `Your current list filters apply (${appliedFilterNames.join(
                                  ', '
                              )}) - ${totalRows} employee${
                                  totalRows === 1 ? '' : 's'
                              } currently match.`
                            : `No list filters applied - the whole directory (${totalRows} employee${
                                  totalRows === 1 ? '' : 's'
                              }) is included.`}
                    </Typography>

                    <GroupLabel>Employees to include</GroupLabel>
                    <Box
                        sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
                    >
                        <FormControlLabel
                            control={
                                <Switch
                                    size='small'
                                    checked={includeResigned}
                                    onChange={(e) =>
                                        setIncludeResigned(e.target.checked)
                                    }
                                />
                            }
                            label={
                                <Box sx={{ ml: 0.5 }}>
                                    <Typography variant='body2'>
                                        Include resigned employees
                                    </Typography>
                                    <Typography
                                        variant='caption'
                                        color='text.secondary'
                                    >
                                        Employees past their last working day
                                    </Typography>
                                </Box>
                            }
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    size='small'
                                    checked={includeInactive}
                                    onChange={(e) =>
                                        setIncludeInactive(e.target.checked)
                                    }
                                />
                            }
                            label={
                                <Box sx={{ ml: 0.5 }}>
                                    <Typography variant='body2'>
                                        Include inactive employees
                                    </Typography>
                                    <Typography
                                        variant='caption'
                                        color='text.secondary'
                                    >
                                        Employees whose user account is disabled
                                    </Typography>
                                </Box>
                            }
                        />
                    </Box>

                    <Divider sx={{ my: 1.5 }} light />

                    <ExportOptionsFields
                        state={optionsState}
                        imagesLabel='Include document links'
                        imagesHint='Adds URL columns for photograph, signature, NID card and other attachments'
                    />

                    {exporting && progress && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mt: 2
                            }}
                        >
                            <CircularProgress size={16} />
                            <Typography
                                variant='caption'
                                color='text.secondary'
                            >
                                {progress}
                            </Typography>
                        </Box>
                    )}
                </Box>

                <Divider sx={{ mt: 2 }} />
                <StyledCardAction>
                    <Button
                        onClick={handleClose}
                        color='tertiary'
                        disabled={exporting}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleExport}
                        loading={exporting}
                        disabled={!optionsState.selectedCount}
                    >
                        Export Excel
                    </Button>
                </StyledCardAction>
            </DialogWrapper>
        </>
    );
}
